import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { readPrefs, usePrefs, useSession } from '@zehnmindai/embed/react';
import { SignIn } from '@/pages/Auth/SignIn.jsx';
import { useLanguageSync } from '@/shared/hooks/useLanguageSync.js';
import { setSignedIn } from './me.js';

// ─────────────────────────────────────────────────────────────
// docs' screens behind sign-in. The SessionProvider (main.jsx) reads who is
// signed in from docs-api (`GET /api/v1/auth/me`) through docs' BFF cookie;
// nobody is, it tries one silent sign-in at zehn-id (a full-page round trip)
// and then reports signed-out. So this gate shows:
//
//   loading     a spinner (also while the silent sign-in is on its way)
//   signed-out  the sign-in page (pages/Auth/SignIn.jsx)
//   signed-in   the screens, once the person is copied where the helpers
//               that are not React read them (shared/auth/me.js)
//
// Their language at docs-api (useLanguageSync writes it) is applied first,
// once per person and page, so the screens start in it — but only while they
// have not chosen one in the install's top bar (the zehn_prefs cookie every
// module shares, which then wins). It goes into that cookie, so the bar,
// i18next and the other modules agree on it. Cyrillic Uzbek stays when
// docs-api says "uz": it keeps one Uzbek for both scripts.
// ─────────────────────────────────────────────────────────────

const LANGUAGES = new Set(['en', 'ru', 'uz']);

export function PageSpinner() {
  return (
    <div className="h-dvh w-full flex items-center justify-center bg-background">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function SignedIn({ children }) {
  const { user, scopes } = useSession();
  const { i18n } = useTranslation();
  const { fromHost, setLanguage } = usePrefs();
  const [readyFor, setReadyFor] = useState(null);
  const languageFor = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const language = String(user.language || '').toLowerCase();
    let ready = Promise.resolve();
    if (languageFor.current !== user.uuid) {
      languageFor.current = user.uuid;
      const shown = i18n.resolvedLanguage || i18n.language || '';
      const current = String(shown).split('-')[0];
      if (LANGUAGES.has(language) && !fromHost && readPrefs().language === null) {
        // "uz" at docs-api keeps the script this page shows.
        setLanguage(language === current ? shown : language);
        if (language !== current)
          ready = Promise.resolve(i18n.changeLanguage(language)).catch(() => {});
      }
    }
    ready.then(() => {
      if (cancelled) return;
      setSignedIn(user, scopes);
      setReadyFor(user.uuid);
    });
    return () => {
      cancelled = true;
    };
  }, [user, scopes, i18n, fromHost, setLanguage]);

  if (readyFor !== user.uuid) return <PageSpinner />;
  return (
    <>
      <LanguageSync user={user} />
      {children}
    </>
  );
}

// Started once the person's language is applied (above), so it never writes
// the language this page happened to start in over theirs.
function LanguageSync({ user }) {
  useLanguageSync(user);
  return null;
}

export function SessionGate({ children }) {
  const session = useSession();
  if (session.status === 'loading') return <PageSpinner />;
  if (session.status === 'signed-out') {
    setSignedIn(null);
    return <SignIn />;
  }
  return <SignedIn>{children ?? <Outlet />}</SignedIn>;
}

export default SessionGate;
